import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Paginated } from '@insurensa/shared';
import {
  Badge,
  Card,
  DataTable,
  PageTitle,
  Property,
  PropertyList,
  type DataTableColumn,
} from '@insurensa/ui';
import { useFormat, useT } from '@insurensa/i18n';
import { api, qs } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';
import { BackLink } from '../components/Layout.js';
import { BlockSkeleton, ErrorAlert } from '../components/ui.js';

interface UserRow {
  id: string;
  email: string | null;
  hasSubmittedLead: boolean;
  emailVerified: boolean;
  leadCount: number;
  offerCount: number;
  createdAt: string;
  lastSeenAt: string | null;
}

/** Replaces Masterdata/users.php. */
export function UsersPage(): JSX.Element {
  const t = useT();
  const fmt = useFormat();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState('');

  const { data, error, isLoading } = useQuery({
    queryKey: ['users', search, page, pageSize],
    queryFn: () => api.get<Paginated<UserRow>>(`/admin/users${qs({ search, page, pageSize })}`),
  });

  const columns: Array<DataTableColumn<UserRow>> = [
    {
      key: 'email',
      header: t('fields.email'),
      value: (row) => row.email ?? t('admin.users.guestNoAccount'),
      className: 'wrap-anywhere',
      render: (row) => (
        <Link to={`/users/${row.id}`}>
          {row.email ?? <em className="text-muted">{t('admin.users.guestNoAccount')}</em>}
        </Link>
      ),
    },
    {
      key: 'emailVerified',
      header: t('fields.verified'),
      value: (row) => (row.emailVerified ? t('common.yes') : t('common.no')),
      render: (row) => (
        <Badge tone={row.emailVerified ? 'success' : 'secondary'}>
          {row.emailVerified ? t('common.yes') : t('common.no')}
        </Badge>
      ),
    },
    { key: 'leadCount', header: t('fields.leads'), numeric: true, value: (row) => String(row.leadCount) },
    { key: 'offerCount', header: t('fields.offers'), numeric: true, value: (row) => String(row.offerCount) },
    {
      key: 'createdAt',
      header: t('fields.registeredAt'),
      className: 'nowrap',
      value: (row) => fmt.dateTime(row.createdAt),
    },
    {
      key: 'lastSeenAt',
      header: t('fields.lastActive'),
      className: 'nowrap',
      value: (row) => fmt.dateTime(row.lastSeenAt),
    },
    {
      key: 'actions',
      header: '',
      sortable: false,
      exportable: false,
      render: (row) => (
        <Link to={`/users/${row.id}`} className="btn btn-primary btn-sm">
          {t('common.open')}
        </Link>
      ),
    },
  ];

  return (
    <>
      <PageTitle
        heading={t('admin.nav.users')}
        subheading={t('admin.users.subheading')}
        icon="pe-7s-users"
      />
      <ErrorAlert error={error} />

      <Card noPadding>
        <div className="card-body">
          <DataTable
            columns={columns}
            rows={data?.items ?? []}
            rowKey={(row) => row.id}
            loading={isLoading}
            exportFileName="users"
            exportTitle={t('admin.users.exportTitle')}
            emptyMessage={t('admin.users.empty')}
            search={{
              value: search,
              onChange: (value) => {
                setSearch(value);
                setPage(1);
              },
            }}
            pagination={{
              page: data?.page ?? page,
              pageSize: data?.pageSize ?? pageSize,
              total: data?.total ?? 0,
              totalPages: data?.totalPages ?? 1,
              onPage: setPage,
              onPageSize: (next) => {
                setPageSize(next);
                setPage(1);
              },
            }}
            onExportAll={async () => {
              const all = await api.get<Paginated<UserRow>>(
                `/admin/users${qs({ search, page: 1, pageSize: 100 })}`,
              );
              return all.items;
            }}
          />
        </div>
      </Card>
    </>
  );
}

interface UserDetail {
  id: string;
  email: string | null;
  /** Signed in anonymously; no address on file. */
  isGuest?: boolean;
  hasSubmittedLead: boolean;
  emailVerified: boolean;
  createdAt: string;
  lastSeenAt: string | null;
  leads: Array<{
    id: string;
    reference: string;
    type: string;
    typeLabel: string;
    city: string | null;
    offerCount: number;
    createdAt: string;
  }>;
}

/** Replaces viewUser.php plus thrashuser.php. */
export function UserDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const t = useT();
  const fmt = useFormat();
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const [confirming, setConfirming] = useState(false);

  const { data, error, isLoading } = useQuery({
    queryKey: ['user', id],
    queryFn: () => api.get<UserDetail>(`/admin/users/${id}`),
    enabled: Boolean(id),
  });

  const remove = useMutation({
    mutationFn: () => api.delete(`/admin/users/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['users'] });
      window.location.assign('/users');
    },
  });

  if (isLoading) {
    return (
      <>
        <PageTitle heading={t('admin.nav.users')} icon="pe-7s-users" />
        <BlockSkeleton height={180} />
      </>
    );
  }

  if (error || !data) {
    return (
      <>
        <PageTitle heading={t('admin.nav.users')} icon="pe-7s-users" />
        <ErrorAlert error={error} />
      </>
    );
  }

  return (
    <>
      <PageTitle
        heading={data.email ?? t('admin.users.guest')}
        subheading={
          data.isGuest ? t('admin.userDetail.guestSubheading') : t('admin.userDetail.accountSubheading')
        }
        icon="pe-7s-user"
        actions={<BackLink to="/users">{t('common.back')}</BackLink>}
      />

      <ErrorAlert error={remove.error} />

      <div className="row">
        <div className="col-lg-8">
          <Card
            title={
              <div className="card-header-title">
                {t('admin.userDetail.leadsWithCount', { count: data.leads.length })}
              </div>
            }
            noPadding
          >
            {data.leads.length === 0 ? (
              <div className="p-4 text-center text-muted">{t('admin.userDetail.noLeads')}</div>
            ) : (
              <table className="table table-hover table-striped mb-0">
                <thead>
                  <tr>
                    <th>{t('common.reference')}</th>
                    <th>{t('fields.insuranceLine')}</th>
                    <th>{t('fields.city')}</th>
                    <th className="text-right">{t('fields.offers')}</th>
                    <th>{t('common.createdAt')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.leads.map((lead) => (
                    <tr key={lead.id}>
                      <td>
                        <Link to={`/leads/${lead.id}`}>{lead.reference.slice(-10)}</Link>
                      </td>
                      <td>
                        <Badge tone="primary">{fmt.leadType(lead.type)}</Badge>
                      </td>
                      <td>{lead.city ?? fmt.empty}</td>
                      <td className="text-right">{lead.offerCount}</td>
                      <td>{fmt.dateTime(lead.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </div>

        <div className="col-lg-4">
          <Card title={<div className="card-header-title">{t('admin.userDetail.account')}</div>}>
            <PropertyList>
              <tbody>
                <Property label={t('fields.email')}>
                  {data.email ?? t('admin.users.guestNoAccount')}
                </Property>
                <Property label={t('fields.verified')}>
                  {data.emailVerified ? t('common.yes') : t('common.no')}
                </Property>
                <Property label={t('fields.registeredAt')}>{fmt.dateTime(data.createdAt)}</Property>
                <Property label={t('fields.lastActive')}>{fmt.dateTime(data.lastSeenAt)}</Property>
              </tbody>
            </PropertyList>
          </Card>

          {can('SUPER_ADMIN', 'ADMIN') && (
            <Card title={<div className="card-header-title">{t('admin.userDetail.deleteAccount')}</div>}>
              <p className="text-muted">
                {t('admin.userDetail.deleteExplanation')}
              </p>
              {confirming ? (
                <>
                  <button
                    type="button"
                    className="btn btn-danger btn-sm mr-2"
                    disabled={remove.isPending}
                    onClick={() => remove.mutate()}
                  >
                    {t('admin.userDetail.deleteConfirm')}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => setConfirming(false)}
                  >
                    {t('common.cancel')}
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm"
                  onClick={() => setConfirming(true)}
                >
                  {t('admin.userDetail.deleteAccount')}
                </button>
              )}
            </Card>
          )}
        </div>
      </div>
    </>
  );
}
